/**
 * Learner home — learner workspace layout.
 *
 * Layout:
 *   Row 1: Creation canvas + learning rail
 *   Row 2: Course path shelf
 *
 * The coach slot — "Your learning" status + ranked suggested sessions — used
 * to be a weakest-list-style `ReviseCard`. Replaced with `CoachCard` which
 * surfaces typed nudges (DUE / DIP / NEW / PUSH / FOCUS) backed by the
 * `/me/suggestions` endpoint and lets the learner start the right session
 * type (learn vs revise) with one click.
 */

import { MyCoursesCard, CoachCard, CreateBox } from './cards';
import { LEARNER_LAYOUT_BREAKPOINTS } from '@/interfaces/learner/theme/layout';
import { PageContainer } from '@/interfaces/learner/ui';

export default function LearnerHome() {
  return (
    <PageContainer
      className="relative pb-24"
      style={{
        fontFamily: 'var(--font-sans)',
        paddingTop: 'var(--page-top-spacing)',
      }}
    >
      <div className="home-layout">
        {/* ── Main workspace: creation + course access left, learning rail right ─── */}
        <section className="home-main-grid">
          <div className="home-left-stack">
            <CreateBox variant="quiet" />
            <MyCoursesCard variant="quiet" />
          </div>

          <div className="home-primary-side">
            <CoachCard />
          </div>
        </section>
      </div>

      <style>{`
        .home-layout {
          display: flex;
          flex-direction: column;
          gap: var(--dashboard-section-gap);
        }

        .home-layout > * { min-width: 0; }

        .home-main-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(360px, var(--dashboard-rail-width));
          gap: var(--dashboard-column-gap);
          align-items: start;
        }

        .home-left-stack {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: var(--dashboard-stack-gap);
        }

        .home-primary-side {
          display: flex;
          flex-direction: column;
          min-height: 360px;
        }

        .home-primary-side > * {
          flex: 1;
        }

        .courses-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        @media (max-width: ${LEARNER_LAYOUT_BREAKPOINTS.doubleDesktopPx - 1}px) {
          .home-main-grid {
            grid-template-columns: 1fr;
          }

          .home-primary-side {
            min-height: 0;
          }
        }

        @media (max-width: ${LEARNER_LAYOUT_BREAKPOINTS.singleDesktopPx - 1}px) {
          .courses-grid { grid-template-columns: 1fr; }
          .home-left-stack { gap: var(--dashboard-stack-gap); }
        }
      `}</style>
    </PageContainer>
  );
}
